import React, { type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../src/lib/utils';
const SERIF = {
  fontFamily: 'var(--font-serif)'
} as const;

/** Signal palette — each colour carries meaning across the whole deck. */
export const INK = {
  blue: '#3B8CFF',
  emerald: '#2FBE8F',
  violet: '#9B7BFF',
  gold: '#D8A55A',
  grey: '#8B95A8'
} as const;
export function Eyebrow({
  children,
  className,
  tone = INK.blue
}: {
  children: ReactNode;
  className?: string;
  tone?: string;
}) {
  return <p className={cn('flex items-center gap-[0.9vw] text-[clamp(8px,0.82vw,17px)] font-medium uppercase tracking-[0.3em]', className)} style={{
    color: tone
  }} data-id="element-24" data-mp="32p9xv695jo32">
      <span aria-hidden className="inline-block h-px w-[2.2vw]" style={{
      background: tone
    }} data-id="element-25" data-mp="3ckla9hx38j7n" />
      {children}
    </p>;
}

/** Cover-scale display type. */
export function Display({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <h1 className={cn('text-[clamp(36px,5.4vw,116px)] font-normal leading-[0.98] tracking-[-0.02em] text-[#F4F7FC]', className)} style={SERIF} data-id="element-26" data-mp="3uf9ccqd3t37u">
      {children}
    </h1>;
}

/** Takeaway slide title. */
export function Title({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <h2 className={cn('text-[clamp(20px,2.55vw,54px)] font-normal leading-[1.12] tracking-[-0.01em] text-[#F4F7FC]', className)} style={SERIF} data-id="element-27" data-mp="3bpucv0d2gm5k">
      {children}
    </h2>;
}
export function Body({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <p className={cn('text-[clamp(11px,1.18vw,25px)] leading-[1.55] text-[#C3CBD9]', className)} data-id="element-28" data-mp="3phqz4ns201u1">
      {children}
    </p>;
}
export function Caption({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <p className={cn('text-[clamp(9px,0.96vw,20px)] leading-[1.5] text-[#8B95A8]', className)} data-id="element-29" data-mp="44de9r838bc7">
      {children}
    </p>;
}
export function Label({
  children,
  className,
  tone
}: {
  children: ReactNode;
  className?: string;
  tone?: string;
}) {
  return <span className={cn('text-[clamp(7.5px,0.76vw,16px)] uppercase tracking-[0.24em]', className)} style={{
    color: tone ?? '#7C8699'
  }} data-id="element-30" data-mp="8xoa6xbd37pc">
      {children}
    </span>;
}

/** Large numeric typography for headline metrics. */
export function Metric({
  value,
  label,
  tone = '#F4F7FC',
  size = 'md',
  className
}: {
  value: string;
  label: string;
  tone?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const sizes = {
    sm: 'text-[clamp(18px,2vw,42px)]',
    md: 'text-[clamp(24px,2.9vw,62px)]',
    lg: 'text-[clamp(34px,4.4vw,94px)]'
  } as const;
  return <div className={className} data-id="element-31" data-mp="10pnec6ynpgt2">
      <p className={cn('leading-[0.95] tracking-[-0.01em]', sizes[size])} style={{
      ...SERIF,
      color: tone
    }} data-id="element-32" data-mp="1941vlfhdokzo">
        {value}
      </p>
      <p className="mt-[1.1vh] text-[clamp(8px,0.84vw,17px)] uppercase tracking-[0.18em] text-[#7C8699]" data-id="element-33" data-mp="1d7hnjyii5x61">
        {label}
      </p>
    </div>;
}
export function Panel({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('border border-white/10 bg-white/[0.028] backdrop-blur-[2px]', className)} data-id="element-34" data-mp="1rw44g4ns2wzd">
      {children}
    </div>;
}

/**
 * Subtle entrance motion for SECONDARY content only — takeaway titles and hero
 * numbers must be legible on first paint, so never wrap those.
 */
export function Reveal({
  children,
  delay = 0,
  className
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  if (reduced) return <div className={className} data-id="element-35" data-mp="2flf9vzg2pfju">{children}</div>;
  return <motion.div className={className} initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-36" data-mp="qykovabt06fy">
      {children}
    </motion.div>;
}